/**
 * Account self-service client.
 *
 * Thin wrappers around the new backend routes. All call ``apiFetch`` so they
 * inherit ``credentials: 'include'`` + bearer-token + drain retry without
 * duplicated boilerplate.  See "Production Request Routing" in CLAUDE.md.
 */
import { apiFetch, apiRequest } from './client'

export interface AccountStatus {
  soft_deleted: boolean
  scheduled_purge_at: string | null
  soft_deleted_at: string | null
  days_remaining: number | null
}

export interface SoftDeleteResponse {
  soft_deleted_at: string
  scheduled_purge_at: string
}

export interface RestoreResponse {
  restored: boolean
}

/** GET /auth/account/status — returns soft-delete status for the current user. */
export async function getAccountStatus(): Promise<AccountStatus> {
  return apiRequest<AccountStatus>('/auth/account/status')
}

/** POST /auth/account/soft-delete — schedules account deletion in 7 days. */
export async function softDeleteAccount(confirmEmail: string): Promise<SoftDeleteResponse> {
  return apiRequest<SoftDeleteResponse>('/auth/account/soft-delete', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ confirm_email: confirmEmail }),
  })
}

/** POST /auth/account/restore — clears the soft-delete row. */
export async function restoreAccount(): Promise<RestoreResponse> {
  return apiRequest<RestoreResponse>('/auth/account/restore', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  })
}

/**
 * Reads the body of a 403 response and returns the soft-delete payload
 * if it carries the ``restore_required`` marker.  Used by the global
 * 403 interceptor in AuthContext to detect when to redirect the user
 * to /account/restore.
 */
export async function tryParseSoftDeleteFromResponse(
  res: Response,
): Promise<{ restoreRequired: true; scheduledPurgeAt: string | null } | null> {
  if (res.status !== 403) return null
  try {
    const cloned = res.clone()
    const data = await cloned.json()
    const detail = data?.detail
    if (detail && typeof detail === 'object' && detail.restore_required === true) {
      return {
        restoreRequired: true,
        scheduledPurgeAt: typeof detail.scheduled_purge_at === 'string' ? detail.scheduled_purge_at : null,
      }
    }
  } catch {
    /* not JSON, not soft-delete */
  }
  return null
}

/**
 * Reads the body of a 401 response and returns true iff it carries the
 * ``AUTH_SESSION_INVALID`` code — a cross-environment / orphan-``sub`` session
 * that the backend rejected (RFC-0851). Used by the global 401 interceptor in
 * AuthContext to sign the user out and route them to /login.
 *
 * Fires ONLY on ``status === 401 && detail.code === 'AUTH_SESSION_INVALID'`` so
 * ordinary/expired-token 401s (and provider/asset 401s, which never carry this
 * code) do NOT nuke the session. Uses its own ``res.clone()`` — the soft-delete
 * parser clones the same response independently, so each handler must read its
 * own copy of the body.
 */
export async function tryParseInvalidSessionFromResponse(res: Response): Promise<boolean> {
  if (res.status !== 401) return false
  try {
    const cloned = res.clone()
    const data = await cloned.json()
    const detail = data?.detail
    if (detail && typeof detail === 'object' && detail.code === 'AUTH_SESSION_INVALID') {
      return true
    }
  } catch {
    /* not JSON, or not the coded invalid-session 401 */
  }
  return false
}

// Re-export the apiFetch escape hatch for callers that need raw responses.
export { apiFetch }
